import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, AlertTriangle, CheckCircle, Calculator } from 'lucide-react';

/**
 * PostingPreviewDialog - Shows calculated posting amounts and allows adjustments
 * before posting a purchase invoice
 */
const PostingPreviewDialog = ({ 
  open, 
  onOpenChange, 
  previewData, 
  loading,
  onConfirm 
}) => {
  const [adjustedTotalUsd, setAdjustedTotalUsd] = useState('');
  const [adjustedTotalLbp, setAdjustedTotalLbp] = useState('');
  const [adjustedVatLbp, setAdjustedVatLbp] = useState('');
  const [effectiveRate, setEffectiveRate] = useState(0);
  const [posting, setPosting] = useState(false);

  // Initialize with calculated values when preview data loads
  useEffect(() => {
    if (previewData?.calculated) {
      setAdjustedTotalUsd(previewData.calculated.total_usd?.toString() || '');
      setAdjustedTotalLbp(previewData.calculated.total_lbp?.toString() || '');
      setAdjustedVatLbp(previewData.calculated.tax_lbp?.toString() || '');
      setEffectiveRate(previewData.exchange_rate || 0);
    }
  }, [previewData]);

  // Auto-calculate effective rate when XOF changes
  useEffect(() => {
    const totalUsd = parseFloat(adjustedTotalUsd) || 0;
    const totalLbp = parseFloat(adjustedTotalLbp) || 0;
    if (totalUsd > 0 && totalLbp > 0) {
      setEffectiveRate(Math.round(totalLbp / totalUsd));
    }
  }, [adjustedTotalUsd, adjustedTotalLbp]);

  const handleConfirm = async () => {
    setPosting(true);
    try {
      const adjustments = {
        adjusted_total_usd: parseFloat(adjustedTotalUsd) || null,
        adjusted_total_lbp: parseFloat(adjustedTotalLbp) || null,
        adjusted_vat_lbp: previewData?.is_vat_applicable ? (parseFloat(adjustedVatLbp) || null) : null
      };
      await onConfirm(adjustments);
    } finally {
      setPosting(false);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  const formatLbp = (num) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  // Check if values have been modified
  const isModified = () => {
    if (!previewData?.calculated) return false;
    const calc = previewData.calculated;
    return (
      parseFloat(adjustedTotalUsd) !== calc.total_usd ||
      parseFloat(adjustedTotalLbp) !== calc.total_lbp ||
      (previewData.is_vat_applicable && parseFloat(adjustedVatLbp) !== calc.tax_lbp)
    );
  };

  // Calculate difference percentage for XOF
  const getLbpDiffPercent = () => {
    if (!previewData?.calculated?.total_lbp) return 0;
    const originalLbp = previewData.calculated.total_lbp;
    const adjustedLbp = parseFloat(adjustedTotalLbp) || 0;
    return Math.abs((adjustedLbp - originalLbp) / originalLbp * 100);
  };

  const diffPercent = getLbpDiffPercent();
  const isOverThreshold = diffPercent > 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="posting-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-cyan-400" />
            Posting Preview
          </DialogTitle>
          {previewData && (
            <p className="text-sm text-muted-foreground">
              Invoice: <span className="font-mono text-cyan-400">{previewData.invoice_number}</span>
              {previewData.supplier_name && (
                <span className="ml-2">| {previewData.supplier_name}</span>
              )}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
          </div>
        ) : previewData ? (
          <div className="space-y-4">
            {/* Calculated Values Section */}
            <div className="p-4 rounded-lg bg-muted/30 border space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Calculated Values</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span>Subtotal EUR:</span>
                <span className="text-right font-mono">${formatNumber(previewData.calculated.subtotal_usd)}</span>
                
                {previewData.is_vat_applicable && (
                  <>
                    <span>Tax ({previewData.tax_percent}%):</span>
                    <span className="text-right font-mono">${formatNumber(previewData.calculated.tax_usd)}</span>
                  </>
                )}
                
                <span className="font-medium">Total EUR:</span>
                <span className="text-right font-mono font-medium">${formatNumber(previewData.calculated.total_usd)}</span>
                
                <div className="col-span-2 border-t my-2"></div>
                
                <span>Exchange Rate:</span>
                <span className="text-right font-mono">{formatLbp(previewData.exchange_rate)} XOF</span>
                
                <span>Subtotal XOF:</span>
                <span className="text-right font-mono">{formatLbp(previewData.calculated.subtotal_lbp)} XOF</span>
                
                {previewData.is_vat_applicable && (
                  <>
                    <span>Tax XOF:</span>
                    <span className="text-right font-mono">{formatLbp(previewData.calculated.tax_lbp)} XOF</span>
                  </>
                )}
                
                <span className="font-medium">Total XOF:</span>
                <span className="text-right font-mono font-medium">{formatLbp(previewData.calculated.total_lbp)} XOF</span>
              </div>
            </div>

            {/* Adjustment Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                Adjust to Match Supplier Invoice
                {isModified() && <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">Modified</span>}
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Total EUR</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={adjustedTotalUsd}
                    onChange={(e) => setAdjustedTotalUsd(e.target.value)}
                    className="font-mono"
                    data-testid="adjusted-total-usd"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Total XOF</Label>
                  <Input
                    type="number"
                    step="1"
                    value={adjustedTotalLbp}
                    onChange={(e) => setAdjustedTotalLbp(e.target.value)}
                    className="font-mono"
                    data-testid="adjusted-total-lbp"
                  />
                </div>
              </div>

              {previewData.is_vat_applicable && (
                <div className="space-y-1">
                  <Label className="text-xs">VAT Amount XOF</Label>
                  <Input
                    type="number"
                    step="1"
                    value={adjustedVatLbp}
                    onChange={(e) => setAdjustedVatLbp(e.target.value)}
                    className="font-mono"
                    data-testid="adjusted-vat-lbp"
                  />
                </div>
              )}

              {/* Effective Exchange Rate */}
              <div className="flex items-center justify-between text-sm p-2 rounded bg-muted/20">
                <span className="text-muted-foreground">Effective Exchange Rate:</span>
                <span className="font-mono font-medium">{formatLbp(effectiveRate)} XOF/EUR</span>
              </div>

              {/* Warning if over threshold */}
              {isOverThreshold && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-400">Adjustment Too Large</p>
                    <p className="text-xs text-muted-foreground">
                      XOF difference is {diffPercent.toFixed(1)}% from calculated. Maximum allowed is 5%.
                    </p>
                  </div>
                </div>
              )}

              {/* Info when modified but within threshold */}
              {isModified() && !isOverThreshold && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-cyan-400">Adjustments Applied</p>
                    <p className="text-xs text-muted-foreground">
                      Values adjusted by {diffPercent.toFixed(1)}% to match supplier invoice.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No preview data available
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={posting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={posting || loading || isOverThreshold}
            data-testid="confirm-post-btn"
          >
            {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Confirm & Post
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostingPreviewDialog;
